/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	EntityType
} from '@shared/implementations/entities/entity-type';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	IActionResponse
} from '@shared/interfaces/workflow/action-response.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SessionService
} from '@shared/services/session.service';

/* eslint-enable max-len */

@Component({
	selector: 'bind-transaction',
	templateUrl: './bind-transaction.component.html',
	styleUrls: []
})

/**
 * A component representing a wizard step for binding a policy term transaction.
 *
 * @export
 * @class BindTransactionComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class BindTransactionComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the bind transaction component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes on entity creation.
	 * @param {EntityService} entityService
	 * The entity service used to lookup entity modules upon creation.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used in this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used in this component.
	 * @param {SessionService} sessionService
	 * The session service used in this component.
	 * @memberof BindTransactionComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public entityService: EntityService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public insuranceService: InsuranceService,
		public sessionService: SessionService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof BindTransactionComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof BindTransactionComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the PolicyTerm entity type to be updated.
	 *
	 * @type {IEntityType}
	 * @memberof BindTransactionComponent
	 */
	public policyTermEntityType: IEntityType;

	/**
	 * Gets or sets the PolicyTermTransaction entity type to be updated.
	 *
	 * @type {IEntityType}
	 * @memberof BindTransactionComponent
	 */
	public policyTermTransactionEntityType: IEntityType;

	/**
	 * Gets or sets the PolicyTerm entity instance associated to the current
	 * data.
	 *
	 * @type {string}
	 * @memberof BindTransactionComponent
	 */
	private policyTermEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the PolicyTermTransaction entity instance associated to the
	 * current data.
	 *
	 * @type {string}
	 * @memberof BindTransactionComponent
	 */
	private policyTermTransactionEntityInstance: IEntityInstance;

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof BindTransactionComponent
	 */
	private currentData: any;

	/**
	 * Gets or sets the workflow action being executed to update the PolicyTerm
	 * data.
	 *
	 * @type {string}
	 * @memberof BindTransactionComponent
	 */
	private readonly workflowActionName: string = 'TransactionBind';

	/**
	 * Implements the on initialization interface.
	 *
	 * @memberof BindTransactionComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData;

		const policyTermTransactionId = this.currentData.data.id;

		this.policyTermEntityType =
			await this.entityTypeApiService
				.getSingleQueryResult(
					'Group eq \'PolicyTerms\' AND Name eq \'PolicyTerm\'',
					AppConstants.empty);

		this.policyTermTransactionEntityType =
			await this.entityTypeApiService
				.getSingleQueryResult(
					`Group eq '${this.currentData.data.entityType}' `
						+ `AND Name eq '${this.currentData.data.entityType}'`,
					AppConstants.empty);

		this.policyTermEntityInstance =
			await this.getPolicyTerm(
				this.policyTermEntityType,
				this.policyTermTransactionEntityType,
				policyTermTransactionId);

		this.policyTermTransactionEntityInstance =
			await this.getPolicyTermTransaction(
				this.policyTermTransactionEntityType,
				policyTermTransactionId);

		this.context.source.addToNext(this.bind.bind(this));

		await this.performPostInitActions();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof BindTransactionComponent
	 */
	public async validityChanged(
		isValid: boolean): Promise<void>
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * Gets the PolicyTerm parent instance data associated to the current
	 * PolicyTermTransaction id collected from the Bind Wizard data.
	 *
	 * @async
	 * @param {IEntityType} policyTermEntityType
	 * The PolicyTerm entity type.
	 * @param {IEntityType} policyTermTransactionEntityType
	 * The PolicyTermTransaction entity type.
	 * @param {number} policyTermTransactionId
	 * The PolicyTermTransaction entity instance id.
	 * @memberof BindTransactionComponent
	 */
	public async getPolicyTerm(
		policyTermEntityType: IEntityType,
		policyTermTransactionEntityType: IEntityType,
		policyTermTransactionId: number): Promise<IEntityInstance>
	{
		this.entityInstanceApiService
			.entityInstanceTypeGroup = policyTermTransactionEntityType.group;

		const entityParents: IEntityInstance[] =
			await this.entityInstanceApiService
				.getParents(
					policyTermTransactionId,
					null,
					null,
					null,
					AppConstants.dataLimits.large,
					policyTermEntityType.group);

		return entityParents[0];
	}

	/**
	 * Gets the PolicyTermTransaction instance data associated to the current
	 * PolicyTermTransaction id collected from the Bind Wizard data.
	 *
	 * @async
	 * @param {IEntityType} policyTermTransactionEntityType
	 * The PolicyTermTransaction entity type.
	 * @param {number} policyTermTransactionId
	 * The PolicyTermTransaction entity instance id.
	 * @memberof BindTransactionComponent
	 */
	public async getPolicyTermTransaction(
		policyTermTransactionEntityType: IEntityType,
		policyTermTransactionId: number): Promise<IEntityInstance>
	{
		this.entityInstanceApiService
			.entityInstanceTypeGroup = policyTermTransactionEntityType.group;

		const policyTermTransactionData: IEntityInstance =
			await this.entityInstanceApiService
				.getSingleQueryResult(
					`id eq ${policyTermTransactionId}`,
					AppConstants.empty);

		return policyTermTransactionData;
	}

	/**
	 * This will send the transaction bind event and navigate to the new
	 * entity.
	 *
	 * @async
	 * @memberof BindTransactionComponent
	 */
	public async bind(): Promise<void>
	{
		await this.transactionBind();

		await this.navigateToPolicyTermDashboard(
			this.policyTermEntityInstance.id,
			this.policyTermEntityType.group);
	}

	/**
	 * Executes the transaction bind process, which updates the PolicyTerm
	 * entity data with the collected data from the Bind Winzard and then
	 * executes the TransacionBind workflow action to update the associated
	 * PolicyTermTransaction data.
	 *
	 * @async
	 * @param {IEntityType} policyTermEntityType
	 * The PolicyTerm entity type.
	 * @param {IEntityInstance} policyTermInstanceData
	 * The PolicyTerm entity instance data to be updated.
	 * @param {IEntityType} policyTermTransactionEntityType
	 * The PolicyTermTransaction entity type.
	 * @param {IEntityInstance} policyTermTransactionInstanceData
	 * The PolicyTermTransaction entity instance data to be updated.
	 * @memberof BindTransactionComponent
	 */
	public async transactionBind()
	{
		const displayName: string =
			new EntityType(this.policyTermTransactionEntityType)
				.displayName;

		setTimeout(() =>
		{
			this.context.source.wizardStepLoading = true;
		});

		await this.activityService.handleActivity(
			new Activity(
				new Promise(async (resolve: any, reject: any) => {
					try {
						await this.executeTransactionBindWorkflow(
							this.policyTermTransactionEntityType,
							this.policyTermTransactionEntityInstance);

						resolve();
					}
					catch (exception)
					{
						reject(exception);
					}
				}),
				'<strong>Binding transaction</strong>',
				'<strong>Transaction bound</strong>',
				`${displayName} has been bound.`,
				`${displayName} has not been bound.`));
	}

	/**
	 * Executes the TransactionBind workflow action.
	 *
	 * @param {IEntityType} policyTermTransactionEntityType
	 * The PolicyTermTransaction entity type.
	 * @param {IEntityInstance} policyTermTransactionInstanceData
	 * The PolicyTermTransaction entity instance data to be updated.
	 * @returns {Promise<number>}
	 * The IActionResponse response from the PolicyTermTransaction update.
	 * @memberof BindTransactionComponent
	 */
	private async executeTransactionBindWorkflow(
		policyTermTransactionEntityType: IEntityType,
		policyTermTransactionInstanceData:
			IEntityInstance): Promise<IActionResponse>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			policyTermTransactionEntityType.group;

		const actionResponse: IActionResponse =
			await this.entityInstanceApiService
				.executeAction(
					policyTermTransactionInstanceData.id,
					this.workflowActionName,
					policyTermTransactionInstanceData,
					this.getQueryString());

		return actionResponse;
	}

	/**
	 * Gets a query string.
	 *
	 * @memberof BindTransactionComponent
	 */
	private getQueryString(): string
	{
		const queryString: string = '?'
			+ `${InsuranceConstants.queryParameters.invoiceTo}=`
			+ this.currentData.data.invoiceTo.resourceIdentifier
			+ `&${InsuranceConstants.queryParameters.invoicingMethod}=`
			+ this.currentData.data.invoicingMethod
			+ `&${InsuranceConstants.queryParameters.enableAutoPayments}=`
			+ this.currentData.data.enableAutomaticInstallmentPayments
			+ `&${InsuranceConstants.queryParameters.electronicDocuments}=`
			+ this.currentData.data.electronicDocuments
			+ `&${InsuranceConstants.queryParameters.paymentPlanResourceId}=`
			+ this.currentData.data.paymentPlan.resourceIdentifier;

		return queryString;
	}

	/**
	 * This will navigate to the policy term dashboard using the policy term id
	 * provided.
	 *
	 * @async
	 * @param {number} entityId
	 * The policy term entity id to navigate.
	 * @param {string} group
	 * The entity group associated to the navigation.
	 * @memberof BindTransactionComponent
	 */
	private async navigateToPolicyTermDashboard(
		entityId: number,
		group: string)
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				group,
				AppConstants.viewTypes.edit,
				entityId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}

	/**
	 * Handles the post initialization action.
	 * This will create the dynamic formly layout for display component creation
	 * and permissions.
	 *
	 * @memberof BindTransactionComponent
	 */
	private async performPostInitActions(): Promise<void>
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				<FormlyFieldConfig>
				{
					key: 'data.invoicingMethod',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Invoicing Method',
						description: 'The invoicing method.',
						disabled: true,
						default: 'Paper',
						options: [
							{
								value: 'Paper',
								label: 'Paper'
							}
						]
					}
				},
				<FormlyFieldConfig>
				{
					key: 'data.enableAutomaticInstallmentPayments',
					type: FormlyConstants.customControls.customInputSwitch,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Enable Automatic Installment Payments',
						default: false
					}
				}
			];
	}
}